import React, { useState, useRef, useEffect } from "react";
import { Loader2, MessageSquare, Send, Network } from "lucide-react";
import { renderMarkdown } from "../lib/markdown";
import { sendConnectionFollowUp } from "../lib/api";
import { toast } from "sonner";

/**
 * ConnectionChat — persistent chat panel for Connect-the-Dots connections.
 *
 * The conversation lives on the connection document server-side (the
 * `messages` array). On each user turn we POST /connect-dots/{id}/message,
 * then poll until the trailing assistant placeholder flips status:done.
 *
 * Props:
 *   • connection — full connection object as returned by /api/connect-dots/{id}
 *                  (must have at least `id`, `analysis`, `items`, optional `messages[]`)
 *   • onUpdate   — callback (updatedConnection) invoked when a turn completes
 *                  so the parent can persist the latest state to localStorage.
 *   • accent     — hex color for chrome (default pink, matches connect-dots theme).
 */
export default function ConnectionChat({ connection, onUpdate, accent = "#f472b6" }) {
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollAnchor = useRef(null);

  // Derive the chat-only view of `messages` — skip the FIRST pair (subject seed
  // + the full analysis) since the page already renders the analysis above us.
  // If `messages` doesn't exist yet (legacy), we show an empty chat.
  const allMessages = connection.messages || [];
  const chatMessages = allMessages.slice(2);

  useEffect(() => {
    scrollAnchor.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [chatMessages.length, loading]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || loading) return;
    setDraft("");
    setLoading(true);
    try {
      const updated = await sendConnectionFollowUp(connection.id, text);
      onUpdate?.(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Follow-up failed";
      toast.error(detail);
      setDraft(text); // restore on failure so user doesn't lose their question
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="connection-chat"
      className="mt-8 border bg-[#0a0a0a]"
      style={{ borderColor: `${accent}55` }}
    >
      <div
        className="px-4 py-2 flex items-center justify-between border-b"
        style={{ borderColor: `${accent}55` }}
      >
        <div className="flex items-center gap-2">
          <MessageSquare size={12} style={{ color: accent }} />
          <span className="font-mono text-[10px] tracking-[0.3em]" style={{ color: accent }}>
            // KEEP DIGGING — ASK FOLLOW-UPS
          </span>
        </div>
        <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
          <Network size={10} /> {chatMessages.length} {chatMessages.length === 1 ? "TURN" : "TURNS"}
        </span>
      </div>

      {chatMessages.length > 0 && (
        <div className="px-4 py-4 space-y-4 max-h-[600px] overflow-y-auto" data-testid="connection-chat-messages">
          {chatMessages.map((m, i) => {
            const isUser = m.role === "user";
            const isPending = m.status === "generating";
            const isError = m.status === "error";
            return (
              <div
                key={i}
                data-testid={`connection-chat-msg-${i}`}
                className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
              >
                <div className="font-mono text-[9px] tracking-[0.25em] mb-1 text-zinc-500">
                  {isUser ? "YOU" : "ANALYST"}
                </div>
                <div
                  className={`max-w-[88%] px-3 py-2 text-[13px] leading-relaxed ${isUser ? "bg-white/5 text-white" : "bg-[#0F0F0F] text-zinc-200"}`}
                  style={{
                    border: `1px solid ${isUser ? "rgba(255,255,255,0.12)" : isError ? "#ff3b3055" : `${accent}33`}`,
                  }}
                >
                  {isUser ? (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  ) : isPending ? (
                    <div className="flex items-center gap-2 text-zinc-500 font-mono text-[10px] tracking-[0.25em]">
                      <Loader2 size={11} className="animate-spin" /> DRAFTING REPLY…
                    </div>
                  ) : (
                    <div
                      className="markdown markdown-compact"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content || "") }}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {loading && chatMessages[chatMessages.length - 1]?.role !== "assistant" && (
            <div className="flex items-center gap-2 text-zinc-500 font-mono text-[10px] tracking-[0.25em]">
              <Loader2 size={11} className="animate-spin" /> ANALYST DRAFTING REPLY…
            </div>
          )}
          <div ref={scrollAnchor} />
        </div>
      )}

      <form
        onSubmit={submit}
        className="px-4 py-3 flex gap-2 border-t"
        style={{ borderColor: `${accent}55` }}
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask a follow-up about this connection…"
          data-testid="connection-chat-input"
          disabled={loading}
          className="flex-1 bg-[#050505] border px-3 py-2 font-mono text-sm text-white placeholder:text-zinc-600 focus:outline-none"
          style={{ borderColor: `${accent}55` }}
        />
        <button
          type="submit"
          disabled={loading || !draft.trim()}
          data-testid="connection-chat-send"
          className="inline-flex items-center gap-1.5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] disabled:opacity-40"
          style={{ background: accent, color: "#050505" }}
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
          {loading ? "WAIT" : "SEND"}
        </button>
      </form>
    </section>
  );
}
